import { useState } from "react";
import { createInviteLink, invitationMessage } from "../lib/invite";

/* ADDING A CLIENT, from the web.
 *
 * ⚠️ **THE SHARE SHEET HAS NO BROWSER ANALOGUE, SO THE HAND-OFF CHANGES SHAPE AND NOT PURPOSE.**
 * The phone hands the coach the message and the system share sheet; a browser has neither. What it
 * does have is the clipboard — so the message is written for them and copied in one click, and they
 * paste it wherever they already talk to that client. `mailto:` was the alternative and it is
 * worse: it opens whatever mail client the machine happens to be configured for, which on a work
 * MacBook is frequently nothing at all.
 *
 * ⚠️ **THE PANEL STAYS OPEN AFTER A SUCCESSFUL INVITE**, because sending the message is the half
 * that actually reaches the client and closing the form the moment the row lands would hide it. The
 * phone learned the same thing: an invitation that "worked" and delivered nothing is the state this
 * whole screen exists to avoid.
 */
export function InviteClient({ coachId, onInvited }: { coachId: string; onInvited: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  /* 🔴 NO FORM (2026-09-22 night, his words): *"why then the coach to add their email address and
     name and surname? Better the clients do these."* The coach asks for a link and sends it; the
     person who opens it brings their own name and address, in the app, and is attached the moment
     they finish signing up. One link is one person — the token is single-use — so a second client
     gets a second link. */
  async function create() {
    setBusy(true);
    setError(null);
    setCopied(false);
    const outcome = await createInviteLink(coachId);
    setBusy(false);
    if (outcome.kind === "failed") { setError(outcome.message); return; }
    setLink(outcome.url);
    onInvited();
  }

  async function copy() {
    if (link === null) return;
    try {
      await navigator.clipboard.writeText(invitationMessage("", "", undefined, link));
      setCopied(true);
    } catch {
      // ⚠️ SAID, NOT SWALLOWED. `navigator.clipboard` is refused outside a secure context and in
      // some embedded browsers, and a Copy button that silently does nothing is worse than none —
      // the coach walks away believing they have the message.
      setError("Your browser would not let us copy it. Select the message and copy it by hand.");
    }
  }

  if (!open) {
    return (
      /* 🔴 THE ROSTER'S ONE PRIMARY, AND THE REASONING IS A MEASUREMENT RATHER THAN A PREFERENCE.
         Everfit's own `Add Client` is white and bordered (zoomed and confirmed on his live
         account, 2026-09-18) — the ONLY filled control on their clients page is the orange
         `Upgrade`, which is a plan CTA we do not have and would not want. So copying them
         faithfully would leave our clients page with no primary at all. Our contract is exactly
         one `.ht-button-primary` per screen, and a roster's one action is adding a client. */
      <button className="ht-button ht-button-primary roster-invite-open" onClick={() => setOpen(true)}>
        Add client
      </button>
    );
  }

  return (
    <section className="ht-panel roster-invite" aria-label="Add client">
      <p className="ht-body">
        Send your client a link. They tap it on their iPhone, get the app and sign up with their own
        details — and they are connected to you the moment they finish.
      </p>

      {error !== null && <div className="signin-form-error" role="alert">{error}</div>}

      {link === null ? (
        <div className="roster-invite-actions">
          <button className="ht-button" type="button" onClick={() => setOpen(false)}>Close</button>
          <button className="ht-button ht-button-primary" type="button" onClick={() => void create()} disabled={busy}>
            {busy ? "Making the link…" : "Create invite link"}
          </button>
        </div>
      ) : (
        <div className="roster-invite-sent">
          <pre className="roster-invite-message">{invitationMessage("", "", undefined, link)}</pre>
          <div className="roster-invite-actions">
            <button className="ht-button" type="button" onClick={() => { setLink(null); setOpen(false); }}>Done</button>
            <button className="ht-button" type="button" onClick={() => void create()} disabled={busy}>
              Another link
            </button>
            <button className="ht-button ht-button-primary" type="button" onClick={() => void copy()}>
              {copied ? "Copied" : "Copy the message"}
            </button>
          </div>
          <p className="ht-field-hint">One link is for one person — it stops working once they join.</p>
        </div>
      )}
    </section>
  );
}
